// src/pages/configuracion/RolesPage.tsx
import { useState } from 'react';
import {
  Card,
  Button,
  Badge,
  Spinner,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui';
import { toast } from '@/components/ui/Toast';
import { Plus, Edit2, Trash2, Shield } from 'lucide-react';
import { useRoles, useEliminarRol } from '@/hooks/queries/useConfiguracion';
import { RolFormModal } from './components/RolFormModal';
import type { RolAdmin } from '@/types';

export function RolesPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRol, setSelectedRol] = useState<RolAdmin | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const { data: roles, isLoading } = useRoles();
  const eliminarMutation = useEliminarRol();

  const handleEdit = (rol: RolAdmin) => {
    setSelectedRol(rol);
    setModalOpen(true);
  };

  const handleCreate = () => {
    setSelectedRol(null);
    setModalOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteConfirmId) return;
    try {
      await eliminarMutation.mutateAsync(deleteConfirmId);
      toast.success('Rol eliminado correctamente');
      setDeleteConfirmId(null);
    } catch {
      // Error handled by global MutationCache.onError
    }
  };

  const rolToDelete = roles?.find((r: RolAdmin) => r.id === deleteConfirmId);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Gestion de Roles</h1>
        <Button onClick={handleCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Rol
        </Button>
      </div>

      {/* Informacion */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <div className="flex gap-3">
          <Shield className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">Acerca de los roles</p>
            <p className="mt-1">
              Los roles definen los permisos de los usuarios en el sistema.
              Los roles de sistema (ADMIN, SOPORTE, CONSULTOR) no pueden ser eliminados.
            </p>
          </div>
        </div>
      </Card>

      {/* Lista de roles */}
      <Card>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : !roles?.length ? (
          <div className="text-center py-12">
            <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No hay roles configurados</p>
          </div>
        ) : (
          <div className="divide-y">
            {roles.map((rol: RolAdmin) => (
              <div key={rol.id} className="p-4 flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <p className="font-medium text-gray-900">{rol.nombre}</p>
                    <code className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600">
                      {rol.codigo}
                    </code>
                    <Badge variant={rol.es_sistema ? 'warning' : 'default'}>
                      {rol.es_sistema ? 'Sistema' : 'Personalizado'}
                    </Badge>
                  </div>
                  {rol.descripcion && (
                    <p className="text-sm text-gray-500 mt-1">{rol.descripcion}</p>
                  )}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {rol.permisos.slice(0, 5).map((permiso: string) => (
                      <Badge key={permiso} variant="info">
                        {permiso}
                      </Badge>
                    ))}
                    {rol.permisos.length > 5 && (
                      <Badge variant="default">
                        +{rol.permisos.length - 5} mas
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="flex gap-1 ml-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(rol)}
                    title="Editar"
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  {!rol.es_sistema && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteConfirmId(rol.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      title="Eliminar"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Dialog de formulario */}
      <RolFormModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedRol(null);
        }}
        rol={selectedRol}
      />

      {/* Dialog de confirmacion de eliminacion */}
      <Dialog open={!!deleteConfirmId} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar eliminacion</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600 mb-4">
            Esta seguro que desea eliminar el rol <strong>{rolToDelete?.nombre}</strong>?
          </p>
          <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-md mb-6">
            Los usuarios con este rol perderan los permisos asociados.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              loading={eliminarMutation.isPending}
            >
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
